import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useStatusBar } from "@/hooks/useStatusBar";

const SOFT_VIOLET = "#C8C8F4";
const ELEC_LAV = "#9090E8";
const PERIWINKLE = "#5050C8";
const CHARCOAL = "#1C1A3E";

const KinMark = () => (
  <div className="relative" style={{ width: 88, height: 88 }}>
    {/* glow ring */}
    <div
      aria-hidden
      className="absolute splash-float"
      style={{
        inset: -16,
        borderRadius: 30,
        background: "radial-gradient(circle, rgba(80,80,200,0.08) 0%, transparent 70%)",
        pointerEvents: "none",
      }}
    />
    <div
      className="relative flex items-center justify-center"
      style={{
        width: 88,
        height: 88,
        borderRadius: 24,
        background: `linear-gradient(145deg, ${CHARCOAL}, #2C2A5A)`,
        border: "2px solid rgba(80,80,200,0.2)",
        boxShadow: "0 16px 48px rgba(0,0,0,0.4), 0 0 40px rgba(80,80,200,0.07)",
      }}
    >
      <span
        style={{
          fontFamily: "'DM Serif Display', serif",
          fontSize: 48,
          color: "#FFFFFF",
          lineHeight: 1,
          marginTop: 2,
        }}
      >
        K
      </span>
      <span
        className="absolute"
        style={{
          top: "14%",
          right: "14%",
          fontSize: 14,
          color: ELEC_LAV,
          opacity: 0.95,
        }}
      >
        ✦
      </span>
    </div>
  </div>
);

const KAvatar = () => (
  <div
    className="flex items-center justify-center flex-shrink-0"
    style={{
      width: 24,
      height: 24,
      borderRadius: "50%",
      background: `linear-gradient(135deg, ${PERIWINKLE}, ${ELEC_LAV})`,
    }}
  >
    <span
      style={{
        fontFamily: "'DM Serif Display', serif",
        fontSize: 12,
        color: "#FFFFFF",
        lineHeight: 1,
      }}
    >
      K
    </span>
  </div>
);

// Chips render inside a single absolutely-positioned container that overlays
// the full screen. We use explicit top (px) and left/right (px or %) values
// so WKWebView (iOS native wrapper) lays them out reliably. We deliberately
// avoid `bottom:` anchoring, viewport units (vh/dvh), and env() inside top.
// The container itself uses paddingTop: env(safe-area-inset-top) so chips
// sit below the iOS status bar / Dynamic Island.
type Chip = { label: string; style: React.CSSProperties; index: number };

const floatingChips: Chip[] = [
  { label: "🍽️ Meal plan",      style: { top: 95,  left: 18 },                                    index: 0 },
  { label: "🗓️ Weekly plan",    style: { top: 130, left: "50%", transform: "translateX(-50%)" }, index: 1 },
  { label: "📧 Work email",     style: { top: 115, right: 14 },                                   index: 2 },
  { label: "🌱 Garden",         style: { top: 195, left: 22 },                                    index: 3 },
  { label: "🛒 Grocery prices", style: { top: 215, right: 18 },                                   index: 4 },
  { label: "💰 Budget",         style: { top: 245, left: "50%", transform: "translateX(-50%)" }, index: 5 },
  { label: "📅 Plan my week",   style: { top: 285, left: 28 },                                    index: 6 },
];

const CHIP_BG = "rgba(255,255,255,0.12)";
const CHIP_BORDER = "rgba(255,255,255,0.25)";
const CHIP_TEXT = "rgba(220,220,250,0.85)";

const SplashLanding = () => {
  const navigate = useNavigate();
  useStatusBar({ color: '1C1A3E', style: 'light' });

  useEffect(() => {
    document.body.style.overflow = "hidden";
    document.body.classList.add("kin-dark-bg");
    return () => {
      document.body.style.overflow = "";
      document.body.classList.remove("kin-dark-bg");
    };
  }, []);

  const handleGetStarted = () => {
    localStorage.setItem("kin_splash_seen", "true");
    navigate("/signup");
  };

  const handleSignIn = () => {
    localStorage.setItem("kin_splash_seen", "true");
    navigate("/signin");
  };

  return (
    <>
      <style>{`
        @keyframes splashFadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes splashLogoIn {
          from { opacity: 0; transform: scale(0.85) translateY(8px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes splashFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        @keyframes splashChipDrift {
          0%, 100% { transform: translate(0, 0); }
          25% { transform: translate(4px, -3px); }
          50% { transform: translate(-3px, 4px); }
          75% { transform: translate(3px, 3px); }
        }
        @keyframes splashChipFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-4px); }
        }
        @keyframes splashOrbPulse {
          0%, 100% { opacity: 0.3; transform: translateX(-50%) scale(1); }
          50% { opacity: 0.5; transform: translateX(-50%) scale(1.08); }
        }
        @keyframes splashShimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .splash-fade {
          opacity: 0;
          animation: splashFadeUp 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .splash-logo-in {
          opacity: 0;
          animation: splashLogoIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .splash-float {
          animation: splashFloat 4s ease-in-out infinite;
        }
        .splash-chip {
          opacity: 0.22;
          animation: splashChipFloat 4s ease-in-out infinite;
        }
        .splash-orb-top {
          animation: splashOrbPulse 8s ease-in-out infinite;
        }
        .splash-shimmer::before {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent);
          background-size: 200% 100%;
          animation: splashShimmer 3s linear infinite;
          border-radius: inherit;
          pointer-events: none;
        }
      `}</style>
      <div
        className="fixed inset-0 flex flex-col"
        style={{
          background: CHARCOAL,
          backgroundColor: CHARCOAL,
          minHeight: "100dvh",
          paddingTop: "env(safe-area-inset-top, 0px)",
          zIndex: 10000,
          overflow: "hidden",
        }}
      >
        {/* Dot grid */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            backgroundImage: "radial-gradient(rgba(255,255,255,0.025) 0.6px, transparent 0.6px)",
            backgroundSize: "40px 40px",
            pointerEvents: "none",
          }}
        />

        {/* Top glow orb */}
        <div
          aria-hidden
          className="absolute splash-orb-top"
          style={{
            top: "-15%",
            left: "50%",
            width: 500,
            height: 500,
            background: "radial-gradient(circle, rgba(80,80,200,0.09) 0%, transparent 65%)",
            pointerEvents: "none",
          }}
        />

        {/* Bottom glow orb */}
        <div
          aria-hidden
          className="absolute"
          style={{
            bottom: "-10%",
            left: "50%",
            transform: "translateX(-50%)",
            width: 400,
            height: 400,
            background: "radial-gradient(circle, rgba(144,144,232,0.06) 0%, transparent 60%)",
            pointerEvents: "none",
          }}
        />

        {/* Floating ghost chips — absolutely positioned overlay covering the
            full screen so chips can drift around the K card. Explicit pixel
            top + pixel/percentage left/right values. No bottom anchoring,
            no viewport units. The root container already applies
            safe-area-inset-top padding, so chip top:0 sits below the iOS
            status bar / Dynamic Island. */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: "none",
            zIndex: 1,
          }}
        >
          {floatingChips.map((c) => (
            <div
              key={`chip-${c.label}`}
              className="splash-chip"
              style={{
                position: "absolute",
                ...c.style,
                background: CHIP_BG,
                border: `1px solid ${CHIP_BORDER}`,
                borderRadius: 12,
                padding: "5px 10px",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 10,
                fontWeight: 500,
                color: CHIP_TEXT,
                whiteSpace: "nowrap",
                animationDelay: `${c.index * 0.5}s`,
                pointerEvents: "none",
              }}
            >
              {c.label}
            </div>
          ))}
        </div>

        {/* Centered content */}
        <div className="relative flex-1 flex flex-col items-center justify-center px-6 text-center" style={{ paddingBottom: 8, zIndex: 3 }}>
          <div className="splash-logo-in" style={{ animationDelay: "0.1s", marginBottom: 24 }}>
            <KinMark />
          </div>

          <h1
            className="splash-fade"
            style={{
              fontFamily: "'DM Serif Display', serif",
              fontSize: 44,
              color: "#FFFFFF",
              lineHeight: 1.1,
              marginBottom: 6,
              animationDelay: "0.25s",
            }}
          >
            Meet{" "}
            <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: "italic", color: ELEC_LAV }}>
              Kin.
            </span>
          </h1>

          <p
            className="splash-fade"
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 17,
              fontWeight: 500,
              color: SOFT_VIOLET,
              lineHeight: 1.5,
              marginBottom: 10,
              maxWidth: 360,
              animationDelay: "0.4s",
            }}
          >
            An AI assistant that actually does the practical things every mom needs.
          </p>

          <p
            className="splash-fade"
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 14,
              fontWeight: 500,
              color: SOFT_VIOLET,
              opacity: 0.85,
              lineHeight: 1.5,
              marginBottom: 20,
              maxWidth: 340,
              animationDelay: "0.55s",
            }}
          >
            Meal plans, weekly schedules, work emails, budgets, and real grocery store prices.<br />All in one place.
          </p>

          {/* Conversation preview */}
          <div className="w-full flex flex-col" style={{ gap: 8, maxWidth: 360, marginBottom: 16 }}>
            <div className="flex justify-end splash-fade" style={{ animationDelay: "0.6s" }}>
              <div
                style={{
                  maxWidth: "82%",
                  background: "linear-gradient(135deg, rgba(80,80,200,0.87), rgba(28,26,62,0.87))",
                  borderRadius: "14px 14px 4px 14px",
                  padding: "9px 13px",
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 13,
                  color: "#FFFFFF",
                  textAlign: "left",
                }}
              >
                What's for dinner? I have chicken and broccoli.
              </div>
            </div>
            <div className="flex justify-start items-end gap-2 splash-fade" style={{ animationDelay: "1.2s" }}>
              <KAvatar />
              <div
                style={{
                  maxWidth: "82%",
                  background: "rgba(255,255,255,0.07)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: "14px 14px 14px 4px",
                  padding: "9px 13px",
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 13,
                  color: SOFT_VIOLET,
                  textAlign: "left",
                }}
              >
                I've got 3 ideas, plus a grocery list for what's missing. Want me to check real store prices too?
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div
          className="relative flex flex-col items-center"
          style={{
            padding: "0 28px calc(28px + env(safe-area-inset-bottom, 0px)) 28px",
            maxWidth: 400,
            margin: "0 auto",
            width: "100%",
            gap: 12,
            zIndex: 4,
          }}
        >
          <button
            onClick={handleGetStarted}
            className="splash-fade splash-shimmer relative overflow-hidden w-full"
            style={{
              padding: "16px 0",
              borderRadius: 16,
              border: "none",
              cursor: "pointer",
              background: `linear-gradient(135deg, ${PERIWINKLE}, ${ELEC_LAV})`,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 17,
              fontWeight: 600,
              color: "#FFFFFF",
              boxShadow: "0 8px 32px rgba(80,80,200,0.27), 0 0 20px rgba(80,80,200,0.13)",
              animationDelay: "0.9s",
            }}
          >
            <span style={{ position: "relative", zIndex: 1 }}>Get Started</span>
          </button>

          <button
            onClick={handleSignIn}
            className="splash-fade"
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 14,
              fontWeight: 500,
              color: ELEC_LAV,
              padding: "6px 12px",
              animationDelay: "1.1s",
            }}
          >
            I already have an account
          </button>

          <div className="splash-fade text-center" style={{ animationDelay: "1.2s" }}>
            <div
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontStyle: "italic",
                fontSize: 12.5,
                color: "rgba(200,200,244,0.55)",
                marginBottom: 4,
              }}
            >
              Still her. Still doing it all. Now with AI.
            </div>
            <div
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 9,
                letterSpacing: "1.5px",
                color: "rgba(200,200,244,0.4)",
              }}
            >
              KINYOUDOITALL.COM
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SplashLanding;
